/**
 * Creazione manuale DDT (Fase 2.2).
 * Emesso (a cliente, numerazione propria) o ricevuto (da fornitore, numero
 * fornitore). Controparte e causale via Combobox searchable; porto e
 * trasporto a cura da lookup classificazioni (nessun valore hardcoded).
 * UI solo da @floraltech/ui.
 */
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Combobox,
  Input,
  Label,
} from '@floraltech/ui';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface Soggetto {
  id: string;
  ragione_sociale: string;
  partita_iva: string | null;
}

interface Classificazione {
  id: string;
  codice: string;
  descrizione: string;
}

interface RigaForm {
  descrizione: string;
  quantita: string;
  prezzo_unitario: string;
}

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
});

function useClassificazioni(sistema: string): Classificazione[] {
  const [items, setItems] = useState<Classificazione[]>([]);
  useEffect(() => {
    void (async () => {
      const res = await fetch(`/api/v1/classificazioni?sistema=${encodeURIComponent(sistema)}`, {
        headers: authHeaders(),
      });
      if (res.ok) setItems(await res.json());
    })();
  }, [sistema]);
  return items;
}

export function NuovoDDT() {
  const navigate = useNavigate();
  const [direzione, setDirezione] = useState<'emesso' | 'ricevuto'>('emesso');
  const [controparteId, setControparteId] = useState('');
  const [controparteInput, setControparteInput] = useState('');
  const [soggetti, setSoggetti] = useState<Soggetto[]>([]);
  const [dataDdt, setDataDdt] = useState(new Date().toISOString().slice(0, 10));
  const [numeroFornitore, setNumeroFornitore] = useState('');

  const causali = useClassificazioni('causale_trasporto');
  const porti = useClassificazioni('porto');
  const trasportoCura = useClassificazioni('trasporto_a_cura');
  const [causaleId, setCausaleId] = useState('');
  const [causaleInput, setCausaleInput] = useState('');
  const [porto, setPorto] = useState('');
  const [trasportoACura, setTrasportoACura] = useState('');
  const [aspettoBeni, setAspettoBeni] = useState('');
  const [vettore, setVettore] = useState('');
  const [colli, setColli] = useState('');

  const [righe, setRighe] = useState<RigaForm[]>([{ descrizione: '', quantita: '1', prezzo_unitario: '0' }]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const tipoSoggetto = direzione === 'emesso' ? 'cliente' : 'fornitore';

  const cercaSoggetti = async (q: string) => {
    const res = await fetch(
      `/api/v1/soggetti?tipo=${tipoSoggetto}&q=${encodeURIComponent(q)}&limit=20`,
      { headers: authHeaders() },
    );
    if (res.ok) {
      const data = await res.json();
      setSoggetti(Array.isArray(data) ? data : (data.items ?? []));
    }
  };

  useEffect(() => {
    setControparteId('');
    setControparteInput('');
    setSoggetti([]);
    void cercaSoggetti('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [direzione]);

  const setRiga = (i: number, campo: keyof RigaForm, val: string) => {
    setRighe((prev) => prev.map((r, idx) => (idx === i ? { ...r, [campo]: val } : r)));
  };
  const aggiungiRiga = () =>
    setRighe((prev) => [...prev, { descrizione: '', quantita: '1', prezzo_unitario: '0' }]);
  const rimuoviRiga = (i: number) => setRighe((prev) => prev.filter((_, idx) => idx !== i));

  const submit = async () => {
    setError(null);
    if (!controparteId) {
      setError(`Seleziona un ${tipoSoggetto}`);
      return;
    }
    if (direzione === 'ricevuto' && !numeroFornitore.trim()) {
      setError('Numero DDT fornitore obbligatorio per un DDT ricevuto');
      return;
    }
    setBusy(true);
    try {
      const causaleDesc = causali.find((c) => c.id === causaleId)?.descrizione ?? null;
      const payload = {
        direzione,
        controparte_id: controparteId,
        data_ddt: dataDdt,
        numero_fornitore: direzione === 'ricevuto' ? numeroFornitore.trim() : null,
        causale_trasporto_id: causaleId || null,
        causale_trasporto_desc: causaleDesc,
        aspetto_beni: aspettoBeni || null,
        porto: porto || null,
        trasporto_a_cura: trasportoACura || null,
        colli: colli ? Number(colli) : null,
        vettore: vettore || null,
        righe: righe
          .filter((r) => r.descrizione.trim())
          .map((r) => ({
            descrizione: r.descrizione.trim(),
            quantita: Number(r.quantita) || 1,
            prezzo_unitario: Number(r.prezzo_unitario) || 0,
          })),
      };
      const res = await fetch('/api/v1/ddt', {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.detail ?? `Errore ${res.status}`);
      }
      const data = await res.json();
      navigate(`/ddt/${data.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore creazione DDT');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          Indietro
        </Button>
        <h1 className="font-outfit text-2xl font-semibold">Nuovo DDT</h1>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <Card>
        <CardHeader>
          <CardTitle>Testata</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <Label>Direzione</Label>
            <select
              className="w-full rounded border bg-transparent px-3 py-2 text-sm"
              value={direzione}
              onChange={(e) => setDirezione(e.target.value as 'emesso' | 'ricevuto')}
            >
              <option value="emesso">Emesso (a cliente)</option>
              <option value="ricevuto">Ricevuto (da fornitore)</option>
            </select>
          </div>
          <div>
            <Label>Data DDT</Label>
            <Input type="date" value={dataDdt} onChange={(e) => setDataDdt(e.target.value)} />
          </div>
          <div className="md:col-span-2">
            <Label>{direzione === 'emesso' ? 'Cliente' : 'Fornitore'}</Label>
            <Combobox
              value={controparteInput}
              onInputChange={(v: string) => {
                setControparteInput(v);
                void cercaSoggetti(v);
              }}
              onSelect={(o) => {
                setControparteId(o.value);
                setControparteInput(o.label);
              }}
              options={soggetti.map((s) => ({
                value: s.id,
                label: s.ragione_sociale,
                sublabel: s.partita_iva ?? undefined,
              }))}
              placeholder={`Cerca ${tipoSoggetto} per nome o P.IVA...`}
            />
          </div>
          {direzione === 'ricevuto' && (
            <div>
              <Label>Numero DDT fornitore</Label>
              <Input
                value={numeroFornitore}
                onChange={(e) => setNumeroFornitore(e.target.value)}
                placeholder="Numero del documento del fornitore"
              />
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Dati trasporto</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="md:col-span-2">
            <Label>Causale trasporto</Label>
            <Combobox
              value={causaleInput}
              onInputChange={setCausaleInput}
              onSelect={(o) => {
                setCausaleId(o.value);
                setCausaleInput(o.label);
              }}
              options={causali
                .filter((c) =>
                  c.descrizione.toLowerCase().includes(causaleInput.toLowerCase()),
                )
                .map((c) => ({ value: c.id, label: c.descrizione }))}
              placeholder="Cerca causale..."
            />
          </div>
          <div>
            <Label>Porto</Label>
            <select
              className="w-full rounded border bg-transparent px-3 py-2 text-sm"
              value={porto}
              onChange={(e) => setPorto(e.target.value)}
            >
              <option value="">-</option>
              {porti.map((p) => (
                <option key={p.id} value={p.codice}>
                  {p.descrizione}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label>Trasporto a cura di</Label>
            <select
              className="w-full rounded border bg-transparent px-3 py-2 text-sm"
              value={trasportoACura}
              onChange={(e) => setTrasportoACura(e.target.value)}
            >
              <option value="">-</option>
              {trasportoCura.map((t) => (
                <option key={t.id} value={t.codice}>
                  {t.descrizione}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label>Aspetto dei beni</Label>
            <Input value={aspettoBeni} onChange={(e) => setAspettoBeni(e.target.value)} />
          </div>
          <div>
            <Label>Vettore</Label>
            <Input value={vettore} onChange={(e) => setVettore(e.target.value)} />
          </div>
          <div>
            <Label>Colli</Label>
            <Input type="number" value={colli} onChange={(e) => setColli(e.target.value)} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Righe</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {righe.map((r, i) => (
            <div key={i} className="grid grid-cols-12 gap-2">
              <div className="col-span-7">
                <Input
                  placeholder="Descrizione"
                  value={r.descrizione}
                  onChange={(e) => setRiga(i, 'descrizione', e.target.value)}
                />
              </div>
              <div className="col-span-2">
                <Input
                  type="number"
                  placeholder="Qta"
                  value={r.quantita}
                  onChange={(e) => setRiga(i, 'quantita', e.target.value)}
                />
              </div>
              <div className="col-span-2">
                <Input
                  type="number"
                  placeholder="Prezzo"
                  value={r.prezzo_unitario}
                  onChange={(e) => setRiga(i, 'prezzo_unitario', e.target.value)}
                />
              </div>
              <div className="col-span-1 flex items-center">
                <button
                  type="button"
                  className="text-sm text-destructive"
                  onClick={() => rimuoviRiga(i)}
                >
                  X
                </button>
              </div>
            </div>
          ))}
          <Button type="button" variant="ghost" size="sm" onClick={aggiungiRiga}>
            + Aggiungi riga
          </Button>
        </CardContent>
      </Card>

      <div className="flex gap-3">
        <Button onClick={submit} disabled={busy}>
          {busy ? 'Salvataggio...' : 'Crea DDT'}
        </Button>
        <Button variant="ghost" onClick={() => navigate(-1)}>
          Annulla
        </Button>
      </div>
    </div>
  );
}

export default NuovoDDT;
